/**
 * Transaction process graph for bookings:
 *   - default-booking
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
  // When a customer makes a booking to a listing, a transaction is
  // created with the initial request-payment transition.
  // At this transition a PaymentIntent is created by Marketplace API.
  // After this transition, the actual payment must be made on client-side directly to Stripe.
  REQUEST_PAYMENT: 'transition/request-payment',
  REQUEST_PAY_LATER_PAYMENT: 'transition/request-pay-later-payment',

  // A customer can also initiate a transaction with an inquiry, and
  // then transition that with a request.
  INQUIRE: 'transition/inquire',
  SEND_CUSTOM_OFFER: 'transition/send-custom-offer',
  WITHDRAW_CUSTOM_OFFER: 'transition/withdraw-custom-offer',
  DECLINE_CUSTOM_OFFER: 'transition/decline-custom-offer',
  ACCEPT_CUSTOM_OFFER: 'transition/accept-custom-offer',
  EXPIRE_CUSTOM_OFFER: 'transition/expire-custom-offer',
  RESCHEDULE_CUSTOM_OFFER: 'transition/reschedule-custom-offer',
  SEND_CUSTOM_OFFER_AFTER_BOOKING_REQUEST: 'transition/send-custom-offer-after-booking-request',
  WITHDRAW_CUSTOM_OFFER_AFTER_BOOKING_REQUEST:
    'transition/withdraw-custom-offer-after-booking-request',
  DECLINE_CUSTOM_OFFER_AFTER_BOOKING_REQUEST:
    'transition/decline-custom-offer-after-booking-request',
  ACCEPT_CUSTOM_OFFER_AFTER_BOOKING_REQUEST: 'transition/accept-custom-offer-after-booking-request',
  EXPIRE_CUSTOM_OFFER_AFTER_BOOKING_REQUEST: 'transition/expire-custom-offer-after-booking-request',
  RESCHEDULE_CUSTOM_OFFER_AFTER_BOOKING_REQUEST:
    'transition/reschedule-custom-offer-after-booking-request',
  SEND_CUSTOM_OFFER_AFTER_DECLINED_BOOKING: 'transition/send-custom-offer-after-declined-booking',
  WITHDRAW_CUSTOM_OFFER_AFTER_DECLINED_BOOKING:
    'transition/withdraw-custom-offer-after-declined-booking',
  DECLINE_CUSTOM_OFFER_AFTER_DECLINED_BOOKING:
    'transition/decline-custom-offer-after-declined-booking',
  ACCEPT_CUSTOM_OFFER_AFTER_DECLINED_BOOKING:
    'transition/accept-custom-offer-after-declined-booking',
  EXPIRE_CUSTOM_OFFER_AFTER_DECLINED_BOOKING:
    'transition/expire-custom-offer-after-declined-booking',
  RESCHEDULE_CUSTOM_OFFER_AFTER_DECLINED_BOOKING:
    'transition/reschedule-custom-offer-after-declined-booking',
  SEND_CUSTOM_OFFER_AFTER_CANCELLED_BOOKING: 'transition/send-custom-offer-after-cancelled-booking',
  WITHDRAW_CUSTOM_OFFER_AFTER_CANCELLED_BOOKING:
    'transition/withdraw-custom-offer-after-cancelled-booking',
  DECLINE_CUSTOM_OFFER_AFTER_CANCELLED_BOOKING:
    'transition/decline-custom-offer-after-cancelled-booking',
  ACCEPT_CUSTOM_OFFER_AFTER_CANCELLED_BOOKING:
    'transition/accept-custom-offer-after-cancelled-booking',
  EXPIRE_CUSTOM_OFFER_AFTER_CANCELLED_BOOKING:
    'transition/expire-custom-offer-after-cancelled-booking',
  RESCHEDULE_CUSTOM_OFFER_AFTER_CANCELLED_BOOKING:
    'transition/reschedule-custom-offer-after-cancelled-booking',
  APPLY_CUSTOM_OFFER_TO_BOOKING_REQUEST: 'transition/apply-custom-offer-to-booking-request',
  APPLY_CUSTOM_OFFER_PAYMENT_TO_BOOKING_REQUEST:
    'transition/apply-custom-offer-payment-to-booking-request',
  RESCHEDULE_CUSTOM_OFFER_PAYMENT: 'transition/reschedule-custom-offer-payment',
  RESCHEDULE_CUSTOM_OFFER_PREAUTHORIZED: 'transition/reschedule-custom-offer-preauthorized',
  RESCHEDULE_CUSTOM_OFFER_PAY_LATER_PAYMENT: 'transition/reschedule-custom-offer-pay-later-payment',
  RESCHEDULE_CUSTOM_OFFER_ACCEPTED_PENDING_PAYMENT:
    'transition/reschedule-custom-offer-accepted-pending-payment',
  RESCHEDULE_CUSTOM_OFFER_BOOKING: 'transition/reschedule-custom-offer-booking',
  REQUEST_PAYMENT_AFTER_INQUIRY: 'transition/request-payment-after-inquiry',
  REQUEST_PAY_LATER_PAYMENT_AFTER_INQUIRY: 'transition/request-pay-later-payment-after-inquiry',
  REQUEST_CUSTOM_OFFER_PAYMENT_AFTER_INQUIRY:
    'transition/request-custom-offer-payment-after-inquiry',
  REQUEST_CUSTOM_OFFER_PAY_LATER_PAYMENT_AFTER_INQUIRY:
    'transition/request-custom-offer-pay-later-payment-after-inquiry',

  // Stripe SDK might need to ask 3D security from customer, in a separate front-end step.
  // Therefore we need to make another transition to Marketplace API,
  // to tell that the payment is confirmed.
  CONFIRM_PAYMENT: 'transition/confirm-payment',
  OPERATOR_CONFIRM_PAYMENT: 'transition/operator-confirm-payment',
  CONFIRM_CUSTOM_OFFER_PAYMENT: 'transition/confirm-custom-offer-payment',
  OPERATOR_CONFIRM_CUSTOM_OFFER_PAYMENT: 'transition/operator-confirm-custom-offer-payment',
  COMPLETE_CUSTOM_OFFER_PAYMENT: 'transition/complete-custom-offer-payment',
  CONFIRM_CUSTOM_OFFER_PAY_LATER_PAYMENT: 'transition/confirm-custom-offer-pay-later-payment',
  CONFIRM_PAY_LATER_PAYMENT: 'transition/confirm-pay-later-payment',
  OPERATOR_CONFIRM_PAY_LATER_PAYMENT: 'transition/operator-confirm-pay-later-payment',

  // If the payment is not confirmed in the time limit set in transaction process (by default 15min)
  // the transaction will expire automatically.
  EXPIRE_PAYMENT: 'transition/expire-payment',
  PAYMENT_FAILED: 'transition/payment-failed',
  RETRY_PAYMENT_AFTER_FAILED: 'transition/retry-payment-after-failed',
  RETRY_PAY_LATER_PAYMENT_AFTER_FAILED: 'transition/retry-pay-later-payment-after-failed',
  RETRY_CUSTOM_OFFER_PAYMENT_AFTER_FAILED: 'transition/retry-custom-offer-payment-after-failed',
  RETRY_CUSTOM_OFFER_PAY_LATER_PAYMENT_AFTER_FAILED:
    'transition/retry-custom-offer-pay-later-payment-after-failed',
  OPERATOR_EXPIRE_PAYMENT: 'transition/operator-expire-payment',

  // When the provider accepts or declines a transaction from the
  // SalePage, it is transitioned with the accept or decline transition.
  ACCEPT: 'transition/accept',
  ACCEPT_PAY_LATER: 'transition/accept-pay-later',
  DECLINE: 'transition/decline',
  DECLINE_PAY_LATER: 'transition/decline-pay-later',

  // The operator can accept or decline the offer on behalf of the provider
  OPERATOR_ACCEPT: 'transition/operator-accept',
  OPERATOR_ACCEPT_PAY_LATER: 'transition/operator-accept-pay-later',
  OPERATOR_DECLINE: 'transition/operator-decline',
  OPERATOR_DECLINE_PAY_LATER: 'transition/operator-decline-pay-later',

  ACCEPTANCE_WINDOW_PASSED: 'transition/acceptance-window-passed',
  PAY_LATER_ACCEPTANCE_WINDOW_PASSED: 'transition/pay-later-acceptance-window-passed',
  OPERATOR_ACCEPTANCE_WINDOW_PASSED: 'transition/operator-acceptance-window-passed',
  OPERATOR_PAY_LATER_ACCEPTANCE_WINDOW_PASSED:
    'transition/operator-pay-later-acceptance-window-passed',
  AUTO_DECLINE_BOOKING_TIME_STARTS: 'transition/auto-decline-booking-time-starts',

  PAYMNENT_WINDOW_PASSED: 'transition/payment-window-passed',
  OPERATOR_PAYMNENT_WINDOW_PASSED: 'transition/operator-payment-window-passed',

  // The backend automatically expire the transaction.
  EXPIRE: 'transition/expire',

  // Admin can also cancel the transition.
  CANCEL: 'transition/cancel',
  CANCEL_BY_CUSTOMER: 'transition/cancel-by-customer',
  CANCEL_BY_PROVIDER: 'transition/cancel-by-provider',
  CANCEL_BOOKING_REQUEST_PAYMENT: 'transition/cancel-booking-request-payment',
  CANCEL_BOOKING_REQUEST: 'transition/cancel-booking-request',
  CANCEL_PAY_LATER_BOOKING_REQUEST: 'transition/cancel-pay-later-booking-request',

  RESCHEDULE_BOOKING: 'transition/reschedule-booking',
  AUTO_COMPLETE_AFTER_RESCHEDULE_BOOKING: 'transition/auto-complete-after-reschedule-booking',
  OPERATOR_COMPLETE_AFTER_RESCHEDULE_BOOKING:
    'transition/operator-complete-after-reschedule-booking',
  ACCEPT_RESCHEDULE_BOOKING: 'transition/accept-reschedule-booking',
  DECLINE_RESCHEDULE_BOOKING: 'transition/decline-reschedule-booking',

  // The backend will mark the transaction completed.
  COMPLETE: 'transition/complete',
  OPERATOR_COMPLETE: 'transition/operator-complete',

  COMPLETE_AFTER_CANCEL: 'transition/complete-after-cancel',
  OPERATOR_COMPLETE_AFTER_CANCEL: 'transition/operator-complete-after-cancel',

  // account info transitions
  REQUEST_ACCOUNT_INFO_BY_PROVIDER: 'transition/request-account-info-by-provider',
  ADD_ACCOUNT_INFO_BY_CUSTOMER: 'transition/add-account-info-by-customer',
  // Reviews are given through transaction transitions. Review 1 can be
  // by provider or customer, and review 2 will be the other party of
  // the transaction.

  DISPUTE: 'transition/dispute',
  OPERATOR_DISPUTE: 'transition/operator-dispute',

  // If nothing is done to disputed transaction it ends up to Cancelled state
  AUTO_CANCEL_FROM_DISPUTED: 'transition/auto-cancel-from-disputed',

  // Operator can cancel disputed transaction manually
  CANCEL_FROM_DISPUTED: 'transition/cancel-from-disputed',

  // Operator can mark the disputed transaction as received
  MARK_RECEIVED_FROM_DISPUTED: 'transition/mark-received-from-disputed',

  PAYOUT_COMPLETE: 'transition/payout-complete',
  OPERATOR_PAYOUT_COMPLETE: 'transition/operator-payout-complete',
  PAYOUT_COMPLETE_AFTER_DISPUTE: 'transition/payout-complete-after-dispute',
  OPERATOR_PAYOUT_COMPLETE_AFTER_DISPUTE: 'transition/operator-payout-complete-after-dispute',

  REVIEW_1_BY_PROVIDER: 'transition/review-1-by-provider',
  REVIEW_2_BY_PROVIDER: 'transition/review-2-by-provider',
  REVIEW_1_BY_CUSTOMER: 'transition/review-1-by-customer',
  REVIEW_2_BY_CUSTOMER: 'transition/review-2-by-customer',
  EXPIRE_CUSTOMER_REVIEW_PERIOD: 'transition/expire-customer-review-period',
  EXPIRE_PROVIDER_REVIEW_PERIOD: 'transition/expire-provider-review-period',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',
};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */
export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  PENDING_PAYMENT: 'pending-payment',
  PENDING_PROVIDER_ACCEPTANCE: 'pending-provider-acceptance',
  PAYMENT_EXPIRED: 'payment-expired',
  PREAUTHORIZED: 'preauthorized',
  BOOKING_RESCHEDULED: 'booking-rescheduled',
  DECLINED: 'declined',
  ACCOUNT_DETAILS_REQUESTED: 'account-details-requested',
  ACCEPTED_PENDING_PAYMENT: 'accepted-pending-payment',
  ACCEPTED: 'accepted',
  EXPIRED: 'expired',
  CANCELLED: 'cancelled',
  COMPLETED: 'completed',
  DISPUTED: 'disputed',
  RECEIVED: 'received',
  DELIVERED: 'delivered',
  REVIEWED: 'reviewed',
  REVIEWED_BY_CUSTOMER: 'reviewed-by-customer',
  REVIEWED_BY_PROVIDER: 'reviewed-by-provider',
};

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'default-booking/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: states.INITIAL,

  // States
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
        [transitions.REQUEST_PAYMENT]: states.PENDING_PAYMENT,
        [transitions.REQUEST_PAY_LATER_PAYMENT]: states.PENDING_PROVIDER_ACCEPTANCE,
      },
    },
    [states.INQUIRY]: {
      on: {
        [transitions.SEND_CUSTOM_OFFER]: states.INQUIRY,
        [transitions.WITHDRAW_CUSTOM_OFFER]: states.INQUIRY,
        [transitions.DECLINE_CUSTOM_OFFER]: states.INQUIRY,
        [transitions.ACCEPT_CUSTOM_OFFER]: states.INQUIRY,
        [transitions.EXPIRE_CUSTOM_OFFER]: states.INQUIRY,
        [transitions.RESCHEDULE_CUSTOM_OFFER]: states.INQUIRY,
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY]: states.PENDING_PAYMENT,
        [transitions.REQUEST_PAY_LATER_PAYMENT_AFTER_INQUIRY]: states.PENDING_PROVIDER_ACCEPTANCE,
        [transitions.REQUEST_CUSTOM_OFFER_PAYMENT_AFTER_INQUIRY]: states.PENDING_PAYMENT,
        [transitions.REQUEST_CUSTOM_OFFER_PAY_LATER_PAYMENT_AFTER_INQUIRY]:
          states.PENDING_PROVIDER_ACCEPTANCE,
      },
    },

    [states.PENDING_PAYMENT]: {
      on: {
        [transitions.CANCEL_BOOKING_REQUEST_PAYMENT]: states.CANCELLED,
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
        [transitions.PAYMENT_FAILED]: states.PAYMENT_EXPIRED,
        [transitions.CONFIRM_PAYMENT]: states.PREAUTHORIZED,
        [transitions.OPERATOR_CONFIRM_PAYMENT]: states.PREAUTHORIZED,
        [transitions.CONFIRM_CUSTOM_OFFER_PAYMENT]: states.PREAUTHORIZED,
        [transitions.OPERATOR_CONFIRM_CUSTOM_OFFER_PAYMENT]: states.PREAUTHORIZED,
        [transitions.RESCHEDULE_CUSTOM_OFFER_PAYMENT]: states.PENDING_PAYMENT,
      },
    },

    [states.PENDING_PROVIDER_ACCEPTANCE]: {
      on: {
        [transitions.SEND_CUSTOM_OFFER_AFTER_BOOKING_REQUEST]: states.PENDING_PROVIDER_ACCEPTANCE,
        [transitions.WITHDRAW_CUSTOM_OFFER_AFTER_BOOKING_REQUEST]:
          states.PENDING_PROVIDER_ACCEPTANCE,
        [transitions.DECLINE_CUSTOM_OFFER_AFTER_BOOKING_REQUEST]:
          states.PENDING_PROVIDER_ACCEPTANCE,
        [transitions.ACCEPT_CUSTOM_OFFER_AFTER_BOOKING_REQUEST]: states.PENDING_PROVIDER_ACCEPTANCE,
        [transitions.EXPIRE_CUSTOM_OFFER_AFTER_BOOKING_REQUEST]: states.PENDING_PROVIDER_ACCEPTANCE,
        [transitions.RESCHEDULE_CUSTOM_OFFER_AFTER_BOOKING_REQUEST]:
          states.PENDING_PROVIDER_ACCEPTANCE,
        [transitions.APPLY_CUSTOM_OFFER_TO_BOOKING_REQUEST]: states.PENDING_PROVIDER_ACCEPTANCE,
        [transitions.APPLY_CUSTOM_OFFER_PAYMENT_TO_BOOKING_REQUEST]: states.PENDING_PAYMENT,
        [transitions.CANCEL_PAY_LATER_BOOKING_REQUEST]: states.CANCELLED,
        [transitions.CONFIRM_CUSTOM_OFFER_PAY_LATER_PAYMENT]: states.ACCEPTED,
        [transitions.RESCHEDULE_CUSTOM_OFFER_PAY_LATER_PAYMENT]: states.PENDING_PROVIDER_ACCEPTANCE,
        [transitions.ACCEPT_PAY_LATER]: states.ACCEPTED_PENDING_PAYMENT,
        [transitions.OPERATOR_ACCEPT_PAY_LATER]: states.ACCEPTED_PENDING_PAYMENT,
        [transitions.DECLINE_PAY_LATER]: states.DECLINED,
        [transitions.OPERATOR_DECLINE_PAY_LATER]: states.DECLINED,
        [transitions.PAY_LATER_ACCEPTANCE_WINDOW_PASSED]: states.EXPIRED,
        [transitions.AUTO_DECLINE_BOOKING_TIME_STARTS]: states.DECLINED,
        [transitions.OPERATOR_PAY_LATER_ACCEPTANCE_WINDOW_PASSED]: states.EXPIRED,
      },
    },

    [states.PAYMENT_EXPIRED]: {
      on: {
        [transitions.RETRY_PAYMENT_AFTER_FAILED]: states.PENDING_PAYMENT,
        [transitions.RETRY_PAY_LATER_PAYMENT_AFTER_FAILED]: states.PENDING_PROVIDER_ACCEPTANCE,
        [transitions.RETRY_CUSTOM_OFFER_PAYMENT_AFTER_FAILED]: states.PENDING_PAYMENT,
        [transitions.RETRY_CUSTOM_OFFER_PAY_LATER_PAYMENT_AFTER_FAILED]:
          states.PENDING_PROVIDER_ACCEPTANCE,
      },
    },
    [states.PREAUTHORIZED]: {
      on: {
        [transitions.CANCEL_BOOKING_REQUEST]: states.CANCELLED,
        [transitions.DECLINE]: states.DECLINED,
        [transitions.OPERATOR_DECLINE]: states.DECLINED,
        [transitions.EXPIRE]: states.EXPIRED,
        [transitions.ACCEPT]: states.ACCEPTED,
        [transitions.OPERATOR_ACCEPT]: states.ACCEPTED,
        [transitions.COMPLETE_CUSTOM_OFFER_PAYMENT]: states.ACCEPTED,
        [transitions.RESCHEDULE_CUSTOM_OFFER_PREAUTHORIZED]: states.PREAUTHORIZED,
        [transitions.ACCEPTANCE_WINDOW_PASSED]: states.DECLINED,
        [transitions.AUTO_DECLINE_BOOKING_TIME_STARTS]: states.DECLINED,
        [transitions.OPERATOR_ACCEPTANCE_WINDOW_PASSED]: states.DECLINED,
      },
    },

    [states.DECLINED]: {
      on: {
        [transitions.SEND_CUSTOM_OFFER_AFTER_DECLINED_BOOKING]: states.DECLINED,
        [transitions.WITHDRAW_CUSTOM_OFFER_AFTER_DECLINED_BOOKING]: states.DECLINED,
        [transitions.DECLINE_CUSTOM_OFFER_AFTER_DECLINED_BOOKING]: states.DECLINED,
        [transitions.ACCEPT_CUSTOM_OFFER_AFTER_DECLINED_BOOKING]: states.DECLINED,
        [transitions.EXPIRE_CUSTOM_OFFER_AFTER_DECLINED_BOOKING]: states.DECLINED,
        [transitions.RESCHEDULE_CUSTOM_OFFER_AFTER_DECLINED_BOOKING]: states.DECLINED,
      },
    },
    [states.EXPIRED]: {},
    [states.ACCEPTED_PENDING_PAYMENT]: {
      on: {
        [transitions.CONFIRM_PAY_LATER_PAYMENT]: states.ACCEPTED,
        [transitions.OPERATOR_CONFIRM_PAY_LATER_PAYMENT]: states.ACCEPTED,
        [transitions.RESCHEDULE_CUSTOM_OFFER_ACCEPTED_PENDING_PAYMENT]:
          states.ACCEPTED_PENDING_PAYMENT,
        [transitions.OPERATOR_EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
        [transitions.PAYMNENT_WINDOW_PASSED]: states.CANCELLED,
        [transitions.OPERATOR_PAYMNENT_WINDOW_PASSED]: states.CANCELLED,
      },
    },
    [states.ACCEPTED]: {
      on: {
        [transitions.REQUEST_ACCOUNT_INFO_BY_PROVIDER]: states.ACCOUNT_DETAILS_REQUESTED,
        [transitions.CANCEL]: states.CANCELLED,
        [transitions.CANCEL_BY_CUSTOMER]: states.CANCELLED,
        [transitions.CANCEL_BY_PROVIDER]: states.CANCELLED,
        [transitions.RESCHEDULE_BOOKING]: states.BOOKING_RESCHEDULED,
        [transitions.RESCHEDULE_CUSTOM_OFFER_BOOKING]: states.ACCEPTED,
        [transitions.COMPLETE]: states.COMPLETED,
        [transitions.OPERATOR_COMPLETE]: states.COMPLETED,
      },
    },
    [states.ACCOUNT_DETAILS_REQUESTED]: {
      on: {
        [transitions.ADD_ACCOUNT_INFO_BY_CUSTOMER]: states.CANCELLED,
      },
    },
    [states.COMPLETED]: {
      on: {
        [transitions.DISPUTE]: states.DISPUTED,
        [transitions.OPERATOR_DISPUTE]: states.DISPUTED,
        [transitions.PAYOUT_COMPLETE]: states.DELIVERED,
        [transitions.OPERATOR_PAYOUT_COMPLETE]: states.DELIVERED,
      },
    },

    [states.DISPUTED]: {
      on: {
        [transitions.MARK_RECEIVED_FROM_DISPUTED]: states.RECEIVED,
        [transitions.CANCEL_FROM_DISPUTED]: states.CANCELLED,
        [transitions.AUTO_CANCEL_FROM_DISPUTED]: states.CANCELLED,
      },
    },

    [states.BOOKING_RESCHEDULED]: {
      on: {
        [transitions.ACCEPT_RESCHEDULE_BOOKING]: states.ACCEPTED,
        [transitions.OPERATOR_COMPLETE_AFTER_RESCHEDULE_BOOKING]: states.COMPLETED,
        [transitions.AUTO_COMPLETE_AFTER_RESCHEDULE_BOOKING]: states.COMPLETED,
        [transitions.DECLINE_RESCHEDULE_BOOKING]: states.ACCEPTED,
      },
    },

    [states.CANCELLED]: {
      on: {
        [transitions.SEND_CUSTOM_OFFER_AFTER_CANCELLED_BOOKING]: states.CANCELLED,
        [transitions.WITHDRAW_CUSTOM_OFFER_AFTER_CANCELLED_BOOKING]: states.CANCELLED,
        [transitions.DECLINE_CUSTOM_OFFER_AFTER_CANCELLED_BOOKING]: states.CANCELLED,
        [transitions.ACCEPT_CUSTOM_OFFER_AFTER_CANCELLED_BOOKING]: states.CANCELLED,
        [transitions.EXPIRE_CUSTOM_OFFER_AFTER_CANCELLED_BOOKING]: states.CANCELLED,
        [transitions.RESCHEDULE_CUSTOM_OFFER_AFTER_CANCELLED_BOOKING]: states.CANCELLED,
        [transitions.COMPLETE_AFTER_CANCEL]: states.DELIVERED,
        [transitions.OPERATOR_COMPLETE_AFTER_CANCEL]: states.DELIVERED,
      },
    },
    [states.RECEIVED]: {
      on: {
        [transitions.PAYOUT_COMPLETE_AFTER_DISPUTE]: states.DELIVERED,
        [transitions.OPERATOR_PAYOUT_COMPLETE_AFTER_DISPUTE]: states.DELIVERED,
      },
    },

    [states.DELIVERED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
      },
    },

    [states.REVIEWED_BY_CUSTOMER]: {
      on: {
        [transitions.REVIEW_2_BY_PROVIDER]: states.REVIEWED,
        [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED_BY_PROVIDER]: {
      on: {
        [transitions.REVIEW_2_BY_CUSTOMER]: states.REVIEWED,
        [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED]: { type: 'final' },
  },
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
export const isRelevantPastTransition = transition => {
  return [
    transitions.ACCEPT,
    transitions.ACCEPT_PAY_LATER,
    transitions.OPERATOR_ACCEPT,
    transitions.OPERATOR_ACCEPT_PAY_LATER,
    transitions.ACCEPTANCE_WINDOW_PASSED,
    transitions.PAY_LATER_ACCEPTANCE_WINDOW_PASSED,
    transitions.AUTO_DECLINE_BOOKING_TIME_STARTS,
    transitions.OPERATOR_ACCEPTANCE_WINDOW_PASSED,
    transitions.OPERATOR_PAY_LATER_ACCEPTANCE_WINDOW_PASSED,
    transitions.PAYMNENT_WINDOW_PASSED,
    transitions.OPERATOR_PAYMNENT_WINDOW_PASSED,
    transitions.ADD_ACCOUNT_INFO_BY_CUSTOMER,
    transitions.AUTO_COMPLETE_AFTER_RESCHEDULE_BOOKING,
    transitions.OPERATOR_COMPLETE_AFTER_RESCHEDULE_BOOKING,
    transitions.REQUEST_ACCOUNT_INFO_BY_PROVIDER,
    transitions.CANCEL,
    transitions.COMPLETE,
    transitions.CANCEL_BOOKING_REQUEST_PAYMENT,
    transitions.CANCEL_BOOKING_REQUEST,
    transitions.CANCEL_PAY_LATER_BOOKING_REQUEST,
    transitions.DISPUTE,
    transitions.OPERATOR_DISPUTE,
    transitions.AUTO_CANCEL_FROM_DISPUTED,
    transitions.CANCEL_FROM_DISPUTED,
    transitions.MARK_RECEIVED_FROM_DISPUTED,
    transitions.PAYOUT_COMPLETE,
    transitions.OPERATOR_PAYOUT_COMPLETE,
    transitions.CANCEL_BY_CUSTOMER,
    transitions.CANCEL_BY_PROVIDER,
    transitions.ACCEPT_RESCHEDULE_BOOKING,
    transitions.DECLINE_RESCHEDULE_BOOKING,
    transitions.RESCHEDULE_BOOKING,
    transitions.OPERATOR_COMPLETE,
    transitions.OPERATOR_COMPLETE_AFTER_CANCEL,
    transitions.COMPLETE_AFTER_CANCEL,
    transitions.CONFIRM_PAYMENT,
    transitions.OPERATOR_CONFIRM_PAYMENT,
    transitions.CONFIRM_PAY_LATER_PAYMENT,
    transitions.OPERATOR_CONFIRM_PAY_LATER_PAYMENT,
    transitions.DECLINE,
    transitions.DECLINE_PAY_LATER,
    transitions.OPERATOR_DECLINE,
    transitions.OPERATOR_DECLINE_PAY_LATER,
    transitions.PAYMENT_FAILED,
    transitions.RETRY_PAYMENT_AFTER_FAILED,
    transitions.RETRY_PAY_LATER_PAYMENT_AFTER_FAILED,
    transitions.EXPIRE,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.OPERATOR_EXPIRE_PAYMENT,
  ].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isCustomerReview = transition => {
  return [transitions.REVIEW_1_BY_CUSTOMER, transitions.REVIEW_2_BY_CUSTOMER].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isProviderReview = transition => {
  return [transitions.REVIEW_1_BY_PROVIDER, transitions.REVIEW_2_BY_PROVIDER].includes(transition);
};

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = transition => {
  return [
    transitions.REQUEST_PAYMENT,
    transitions.REQUEST_PAYMENT_AFTER_INQUIRY,
    transitions.RETRY_PAYMENT_AFTER_FAILED,
    transitions.RETRY_CUSTOM_OFFER_PAYMENT_AFTER_FAILED,
    transitions.REQUEST_PAY_LATER_PAYMENT,
    transitions.REQUEST_PAY_LATER_PAYMENT_AFTER_INQUIRY,
    transitions.RETRY_PAY_LATER_PAYMENT_AFTER_FAILED,
    transitions.REQUEST_CUSTOM_OFFER_PAYMENT_AFTER_INQUIRY,
    transitions.REQUEST_CUSTOM_OFFER_PAY_LATER_PAYMENT_AFTER_INQUIRY,
    transitions.RETRY_CUSTOM_OFFER_PAY_LATER_PAYMENT_AFTER_FAILED,
    transitions.APPLY_CUSTOM_OFFER_TO_BOOKING_REQUEST,
    transitions.APPLY_CUSTOM_OFFER_PAYMENT_TO_BOOKING_REQUEST,
  ].includes(transition);
};

// Check when transaction is completed (booking over)
export const isCompleted = transition => {
  const txCompletedTransitions = [
    transitions.COMPLETE,
    transitions.OPERATOR_COMPLETE,
    transitions.OPERATOR_COMPLETE_AFTER_CANCEL,
    transitions.COMPLETE_AFTER_CANCEL,
    transitions.AUTO_COMPLETE_AFTER_RESCHEDULE_BOOKING,
    transitions.OPERATOR_COMPLETE_AFTER_RESCHEDULE_BOOKING,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.EXPIRE_REVIEW_PERIOD,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
  ];
  return txCompletedTransitions.includes(transition);
};

// Check when transaction is refunded (booking did not happen)
// In these transitions action/stripe-refund-payment is called
export const isRefunded = transition => {
  const txRefundedTransitions = [
    transitions.EXPIRE_PAYMENT,
    transitions.PAYMENT_FAILED,
    transitions.OPERATOR_EXPIRE_PAYMENT,
    transitions.PAYMNENT_WINDOW_PASSED,
    transitions.OPERATOR_PAYMNENT_WINDOW_PASSED,
    transitions.EXPIRE,
    transitions.CANCEL,
    transitions.CANCEL_BY_CUSTOMER,
    transitions.CANCEL_BY_PROVIDER,
    transitions.DECLINE,
    transitions.DECLINE_PAY_LATER,
    transitions.OPERATOR_DECLINE,
    transitions.OPERATOR_DECLINE_PAY_LATER,
  ];
  return txRefundedTransitions.includes(transition);
};

export const statesNeedingProviderAttention = [
  states.PREAUTHORIZED,
  states.PENDING_PROVIDER_ACCEPTANCE,
];
export const statesNeedingCustomerAttention = [
  states.PENDING_PAYMENT,
  states.PAYMENT_EXPIRED,
  states.ACCEPTED_PENDING_PAYMENT,
];
